import { useCallback, useEffect, useState } from "react";

/* ==========================================================================
   Night mode.

   Three states, not two. "system" is the default and follows the OS, which is
   what someone who has already set their machine to dark at 9pm expects. An
   explicit choice overrides it and is remembered.

   The choice lives on <html> as data-theme rather than on .ai-root, for two
   reasons: the <body> background sits outside .ai-root and would otherwise stay
   light behind an overscroll, and `color-scheme` has to be set on the root
   element to get dark native scrollbars and <select> popups.

   localStorage is fine for this. The rule in api.js — nothing in localStorage —
   is about credentials, not preferences: a theme is not a token.
   ========================================================================== */

const KEY = "ai-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

const systemPrefersDark = () =>
  typeof window !== "undefined" && window.matchMedia?.(DARK_QUERY).matches === true;

function read() {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    // Private browsing, or storage disabled by policy. Following the OS is a
    // perfectly good fallback, so this must not throw the app away.
    return null;
  }
}

function write(choice) {
  try {
    if (choice) localStorage.setItem(KEY, choice);
    else localStorage.removeItem(KEY);
  } catch {
    /* preference simply won't persist */
  }
}

// Applied to <html> before React renders (see the call at the bottom) so the
// first paint is already the right colour — no white flash on a dark screen.
export function applyTheme(choice) {
  const html = document.documentElement;
  if (choice) html.setAttribute("data-theme", choice);
  else html.removeAttribute("data-theme");
}

export function useTheme() {
  const [choice, setChoice] = useState(read);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Only matters while the choice is "system", but the listener is cheap and
  // unconditional is simpler than adding and removing it.
  useEffect(() => {
    const mq = window.matchMedia?.(DARK_QUERY);
    if (!mq) return;
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    applyTheme(choice);
    write(choice);
  }, [choice]);

  const resolved = choice ?? (systemDark ? "dark" : "light");

  // One button, and it does the obvious thing: give me the other one. Clicking
  // always produces an explicit choice, so it stops tracking the OS from then
  // on — which is what someone overriding it is asking for.
  const toggle = useCallback(() => {
    setChoice(resolved === "dark" ? "light" : "dark");
  }, [resolved]);

  // Lets someone hand control back to the OS without clearing site data.
  const useSystem = useCallback(() => setChoice(null), []);

  return { theme: resolved, isExplicit: choice !== null, toggle, useSystem };
}

// Run at import time, before the first render.
applyTheme(read());
